import type { ModuleSearchResult } from '@/lib/api/search'
import { useEffect, useMemo, useRef, useState } from 'react'
import { RecordingPermissionsDialog } from '@/components/desktop/RecordingPermissionsDialog'
import { useIsSidebarThemedChrome } from '@/components/layout/floating-top-bar'
import { LastRecordingRow } from '@/components/meetings/LastRecordingRow'
import { LiveCaptionsSwitch } from '@/components/meetings/LiveCaptionsSwitch'
import { RecordingElapsed } from '@/components/meetings/RecordingElapsed'
import { RecordingSectionToggle } from '@/components/meetings/RecordingSectionToggle'
import { SummaryTemplateSelect } from '@/components/meetings/SummaryTemplateSelect'
import { useCaptionModel } from '@/components/meetings/useCaptionModel'
import { useLatestRecording } from '@/components/meetings/useLatestRecording'
import { AICreditNotice } from '@/components/shared/ai/AICreditNotice'
import { useTheme } from '@/components/theme-provider'
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAICredits } from '@/hooks/useAICredits'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePresentationVisibility } from '@/hooks/usePresentationVisibility'
import { useRecordingControls } from '@/hooks/useRecordingControls'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { meetingRecordingsApi } from '@/lib/api/meeting-recordings.api'
import { meetingSummaryTemplatesApi } from '@/lib/api/meeting-summary-templates.api'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { HIGHLIGHT_TINT, POPOVER_CARD } from '@/lib/surfaces'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import {
  checkRecordingPermissions,
  getRecordingSupport,
  getSystemAudioStatus,
  pauseRecording,
  resolveLiveCaptionsEnabled,
  resumeRecording,
  startRecording,
  stopRecording,
  updateRecordingLink,
  updateRecordingTemplate,
} from '@/lib/utils/tauri-recording'
import {
  fileExtensionForMimeType,
  isWebRecordingSupported,
  WebMicRecorder,
} from '@/lib/utils/web-recorder'
import {
  announceRecordingStarted,
  copyRecordingNotice,
  PLAN_LOCKED_MESSAGE,
  preflightRecordingStart,
  reportPreflightFailure,
  useMeetingRecorderAvailability,
} from '@/modules/meeting-recorder'
import { useOrganizationStore } from '@/store/organization.store'
import {
  clearStoredScratchpad,
  isLiveCaptionsPreferred,
  setLiveCaptionsPreferred,
  useRecordingStore,
} from '@/store/recording.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import {
  CaretRightIcon,
  ChatCircleTextIcon,
  CircleNotchIcon,
  ListBulletsIcon,
  LockSimpleIcon,
  PauseIcon,
  PencilSimpleLineIcon,
  PlayIcon,
  RecordIcon,
  SidebarSimpleIcon,
  SparkleIcon,
  StopIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { ModuleTypeEnum } from '@scrambl/shared'

// Two tones: the popover is the canvas, each group of content a white card
// on it (corners concentric with the popover's), actions sit on the canvas.
// Clipped: the foldable section rows paint hover fills to the card's corners.
const MENU_CARD = `${POPOVER_CARD} overflow-hidden`
// The last recording reads as status, not settings: tinted, not white.
const LAST_RECORDING_CARD = `${HIGHLIGHT_TINT} overflow-hidden rounded-control`

// Hard cap on a single recording session (matches MAX_RECORDING_MS in the
// desktop Rust recorder) — auto-stops so a forgotten recording can't run all
// day. The audio captured so far is still uploaded and processed.
const MAX_RECORDING_MS = 2 * 60 * 60 * 1000

function VuBars({ level }: { level: number }) {
  const bars = 5
  const active = Math.min(bars, Math.round(level * bars * 4))
  return (
    <div className="flex items-end gap-0.5" aria-hidden>
      {Array.from({ length: bars }, (_, index) => (
        <span
          key={index}
          className={cn(
            'w-0.5 rounded-full transition-colors',
            index < active ? 'bg-red-500' : 'bg-current opacity-25',
          )}
          style={{ height: `${5 + index * 2}px` }}
        />
      ))}
    </div>
  )
}

/** Shared pill styling for the title-bar / header record widget (adapts to
 *  the desktop title-bar theme; plain dark-on-light in the web header). */
function useRecordPillTextMode(): boolean {
  const isMobile = useIsMobile()
  const { theme } = useTheme()
  const sidebarTheme = useUIPreferencesStore(s => s.sidebarTheme)
  const isDarkMode = useResolvedDarkMode(theme)
  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
  const isSidebarThemedChrome = useIsSidebarThemedChrome()
  return (isTauri() && !isMobile) || isSidebarThemedChrome
    ? !isDarkMode && themeConfig.lightTextColor === 'dark'
    : !isDarkMode
}

/**
 * Plan gate for the recorder. Personal spaces and orgs without the
 * `meeting_notes` flag see nothing; orgs whose plan lacks `meeting_recorder`
 * see a neutral locked pill (no upsell). An in-flight recording always keeps
 * its controls, even if the plan changes mid-meeting.
 */
export function RecordButton() {
  const state = useRecordingStore(s => s.state)
  const { availability } = useMeetingRecorderAvailability()
  if (state === 'idle' && availability === 'hidden') return null
  if (state === 'idle' && availability === 'locked') {
    return <LockedRecordButton />
  }
  return <ActiveRecordButton />
}

/** Neutral locked state: explains the plan requirement, nothing to buy here. */
export function LockedRecordButton() {
  const useDarkText = useRecordPillTextMode()
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-record-button-locked
          className={cn(
            'h-8 gap-1.5 border px-3 text-sm opacity-70',
            useDarkText
              ? 'border-black/20 bg-black/5 text-black hover:bg-black/10'
              : 'border-white/20 bg-white/5 text-white hover:bg-white/10',
          )}
          title="Record meeting (not included in your plan)"
        >
          <LockSimpleIcon className="h-4 w-4" />
          <span>Record</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(18rem,calc(100vw-2rem))] p-4"
        align="end"
      >
        <p className="text-sm font-medium">Record meetings</p>
        <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
          {PLAN_LOCKED_MESSAGE} Ask a workspace admin if you need it.
        </p>
      </PopoverContent>
    </Popover>
  )
}

function RecordingVuBars({
  source,
}: {
  source: 'combined' | 'mic' | 'system'
}) {
  const visible = usePresentationVisibility()
  const level = useRecordingStore(s =>
    !visible
      ? 0
      : source === 'combined'
        ? Math.max(s.levels.mic, s.levels.system)
        : s.levels[source],
  )
  return <VuBars level={level} />
}

/**
 * Title-bar record widget — mirrors the TimeEntryWidget pattern. Idle:
 * record button + popover with an optional module picker ("which record
 * should the AI note be tied to"). Recording: red dot, elapsed time, VU
 * meter, pause/stop controls.
 *
 * Desktop (Tauri): full capture via the native recorder (mic + system
 * audio). Web browsers: limited support — mic-only via MediaRecorder, with
 * a hint to use the desktop app for the full experience.
 */
function ActiveRecordButton() {
  const state = useRecordingStore(s => s.state)
  const systemAudioCaptured = useRecordingStore(s => s.systemAudioCaptured)
  const linkedModule = useRecordingStore(s => s.linkedModule)
  const setLinkedModule = useRecordingStore(s => s.setLinkedModule)
  const summaryTemplate = useRecordingStore(s => s.summaryTemplate)
  const setSummaryTemplate = useRecordingStore(s => s.setSummaryTemplate)
  const activeEventKey = useRecordingStore(s => s.activeEventKey)
  const scratchpad = useRecordingStore(s => s.scratchpad)
  const captions = useRecordingStore(s => s.captions)
  const captionsActive = useRecordingStore(s => s.captionsActive)

  // Live captions preference + on-device model state (desktop only)
  const {
    model: captionModel,
    downloadPct: captionDownloadPct,
    ensureReady: ensureCaptionModel,
  } = useCaptionModel()
  const [liveCaptionsEnabled, setLiveCaptionsEnabled] = useState(
    isLiveCaptionsPreferred,
  )
  const [catchUpOpen, setCatchUpOpen] = useState(false)
  const [catchUpSummary, setCatchUpSummary] = useState<string | null>(null)
  const [isCatchingUp, setIsCatchingUp] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  // Secondary sections stay folded while recording until the user opens them.
  const [showCaptions, setShowCaptions] = useState(false)
  const [showNoteSettings, setShowNoteSettings] = useState(false)
  const [isSupported, setIsSupported] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const { checkFeatureFlag } = useFeatureFlags()

  // Same pill styling as the timer widget next to this button
  const useDarkText = useRecordPillTextMode()

  const isDesktop = isTauri()
  const queryClient = useQueryClient()
  const organizationId = useOrganizationStore(s => s.currentOrganization?.id)
  const orgSlug = useOrganizationStore(s => s.currentOrganization?.slug)
  const navigate = useNavigate()
  const webRecorderRef = useRef<WebMicRecorder | null>(null)
  const webRecordingIdRef = useRef<string | null>(null)
  const systemAudioModeRef = useRef<string>('none')
  const applyStatePayload = useRecordingStore(s => s.applyStatePayload)
  const setLevels = useRecordingStore(s => s.setLevels)

  useEffect(() => {
    if (isTauri()) {
      void getRecordingSupport().then(support => {
        setIsSupported(support.supported)
        systemAudioModeRef.current = support.systemAudioMode
      })
    } else {
      setIsSupported(isWebRecordingSupported())
    }
  }, [])

  const handleCaptionsToggle = async (checked: boolean) => {
    if (!checked) {
      setLiveCaptionsEnabled(false)
      setLiveCaptionsPreferred(false)
      return
    }
    // First enable downloads the on-device model (one-time, ~142MB)
    if (await ensureCaptionModel()) {
      setLiveCaptionsEnabled(true)
      setLiveCaptionsPreferred(true)
    }
  }

  const handleCatchUp = async () => {
    if (catchUpCredits?.canUse === false) {
      toast.error('Not enough AI credits for a catch-up summary')
      return
    }
    const transcript = useRecordingStore
      .getState()
      .captions.map(c =>
        c.speaker === 'you'
          ? `You: ${c.text}`
          : c.speaker === 'others'
            ? `Them: ${c.text}`
            : c.text,
      )
      .join('\n')
    setCatchUpOpen(true)
    setIsCatchingUp(true)
    setCatchUpSummary(null)
    try {
      const { summary } = await meetingRecordingsApi.catchUp(transcript)
      setCatchUpSummary(summary)
      void refetchCatchUpCredits()
    } catch {
      setCatchUpSummary(
        "Couldn't generate a catch-up summary — try again in a moment.",
      )
      void refetchCatchUpCredits()
    } finally {
      setIsCatchingUp(false)
    }
  }

  // Stop an in-flight web recording when the component unmounts
  useEffect(() => {
    return () => webRecorderRef.current?.cancel()
  }, [])

  const isRecording = state === 'recording'
  const isPaused = state === 'paused'
  const isActive = isRecording || isPaused || state === 'finalizing'
  const latest = useLatestRecording({
    enabled: isSupported && checkFeatureFlag('MEETING_NOTES'),
  })
  // Finished notes waiting to be read: flagged on the idle pill
  const hasNotesToReview = !isActive && latest.isReadyToReview
  const { data: catchUpCredits, refetch: refetchCatchUpCredits } = useAICredits(
    'meeting_catch_up',
    isActive,
  )

  const linkValue = useMemo(
    () =>
      linkedModule
        ? {
            moduleType: linkedModule.moduleType as ModuleTypeEnum,
            moduleId: linkedModule.moduleId,
            moduleName: linkedModule.moduleName,
          }
        : null,
    [linkedModule],
  )

  const handleModuleSelect = (result: ModuleSearchResult | null) => {
    const next = result
      ? {
          moduleType: result.type,
          moduleId: result.id,
          moduleName: result.name ?? '',
        }
      : null
    setLinkedModule(next)
    // Desktop persists mid-recording changes on the Rust side; the web
    // recorder sends the link with the upload instead.
    if (isDesktop && isActive) {
      void updateRecordingLink(
        next?.moduleType ?? null,
        next?.moduleId ?? null,
      ).catch(() => {})
    }
  }

  const handleTemplateChange = (templateRef: string | null) => {
    setSummaryTemplate(templateRef)
    // Desktop persists mid-recording changes on the Rust side; the web
    // recorder sends the template with the upload instead.
    if (isDesktop && isActive) {
      void updateRecordingTemplate(templateRef).catch(() => {})
    }
    // Recording was started from a calendar meeting — remember the choice as
    // that meeting series' default.
    if (activeEventKey) {
      void meetingSummaryTemplatesApi
        .setPreference(activeEventKey, templateRef)
        .then(() => toast.success('Saved as the default for this meeting'))
        .catch(() => {})
    }
  }

  const syncWebState = (
    state: 'idle' | 'recording' | 'paused' | 'finalizing',
    durationMs = webRecorderRef.current?.durationMs ?? 0,
  ) => {
    applyStatePayload({
      state,
      recordingId:
        state === 'idle' ? undefined : (webRecordingIdRef.current ?? undefined),
      startedAt: undefined,
      durationMs,
      pausedMs: 0,
    })
  }

  const handleWebStart = async (): Promise<boolean> => {
    const recorder = new WebMicRecorder({
      onLevel: level => setLevels({ mic: level, system: 0 }),
      onDuration: durationMs => {
        syncWebState('recording', durationMs)
        // Auto-stop at the session limit so a forgotten recording can't run
        // all day. The audio so far is still uploaded and processed.
        if (durationMs >= MAX_RECORDING_MS && webRecorderRef.current) {
          toast.info('Recording reached the 2-hour limit', {
            description:
              'Stopping and generating notes from what was captured.',
          })
          void handleStop()
        }
      },
    })
    try {
      await recorder.start()
    } catch (error) {
      const isDenied =
        error instanceof DOMException &&
        (error.name === 'NotAllowedError' || error.name === 'SecurityError')
      toast.error(
        isDenied ? 'Microphone access denied' : "Couldn't start recording",
        {
          description: isDenied
            ? 'Allow microphone access for this site in your browser settings, then try again.'
            : String(error instanceof Error ? error.message : error).slice(
                0,
                200,
              ),
        },
      )
      return false
    }
    webRecorderRef.current = recorder
    // A real UUID doubles as the scratchpad image upload scope. The previous
    // static "web" id could not be stored in Document.moduleId (UUID).
    webRecordingIdRef.current = crypto.randomUUID()
    syncWebState('recording', 0)
    return true
  }

  const handleWebStop = async () => {
    const recorder = webRecorderRef.current
    if (!recorder) return
    const webRecordingId = webRecordingIdRef.current
    webRecorderRef.current = null
    syncWebState('finalizing')
    try {
      const { blob, mimeType, durationMs } = await recorder.stop()
      const durationSeconds = Math.max(1, Math.round(durationMs / 1000))
      const extension = fileExtensionForMimeType(mimeType)
      const upload = meetingRecordingsApi
        .uploadAudioBlob(blob, {
          fileName: `recording-${Date.now()}.${extension}`,
          mimeType: mimeType.split(';')[0],
          durationSeconds,
          moduleType: linkedModule?.moduleType,
          moduleId: linkedModule?.moduleId,
          summaryTemplate:
            useRecordingStore.getState().summaryTemplate ?? undefined,
          userNotes:
            useRecordingStore.getState().scratchpad.trim() || undefined,
        })
        .then(result => {
          if (webRecordingId) clearStoredScratchpad(webRecordingId)
          // Make the new recording show up immediately on the Meetings tab
          void queryClient.invalidateQueries({
            queryKey: ['meeting-recordings'],
          })
          return result
        })
        .finally(() => {
          syncWebState('idle')
          webRecordingIdRef.current = null
        })
      toast.promise(upload, {
        loading: 'Uploading recording…',
        success: 'Recording uploaded — Benny is writing the notes…',
        error: error =>
          getMutationErrorMessage(error, "Couldn't upload the recording."),
      })
      await upload
    } catch {
      // toast.promise already surfaced the failure
      syncWebState('idle')
      webRecordingIdRef.current = null
    }
  }

  const needsSystemAudioPrompt = async () => {
    if (systemAudioModeRef.current !== 'tap') return
    const status = await getSystemAudioStatus().catch(() => 'undetermined')
    return status === 'undetermined'
  }

  const handleStart = async () => {
    setIsBusy(true)
    try {
      // Plan + consent BEFORE any audio is captured — the API would refuse
      // the upload afterwards, and the user would lose the meeting.
      const preflight = await preflightRecordingStart({
        queryClient,
        organizationId,
        surface: isDesktop ? 'desktop' : 'web',
      })
      if (!preflight.ok) {
        reportPreflightFailure(preflight)
        return
      }
      if (!isDesktop) {
        const started = await handleWebStart()
        setIsOpen(false)
        if (started) {
          announceRecordingStarted({
            isOutOfCredits: preflight.isOutOfCredits,
          })
        }
        return
      }
      const permissions = await checkRecordingPermissions()
      if (permissions.microphone !== 'granted') {
        setShowPermissionsDialog(true)
        return
      }
      if (await needsSystemAudioPrompt()) {
        setShowPermissionsDialog(true)
        return
      }
      await startRecording({
        moduleType: linkedModule?.moduleType,
        moduleId: linkedModule?.moduleId,
        summaryTemplate: summaryTemplate ?? undefined,
        // Fresh read, not the cached checkbox state — captionModel is null
        // until the on-mount fetch resolves, which used to silently start
        // captionless right after a window (re)mount.
        liveCaptions: await resolveLiveCaptionsEnabled(),
      })
      setIsOpen(false)
      announceRecordingStarted({ isOutOfCredits: preflight.isOutOfCredits })
    } catch (error) {
      toast.error("Couldn't start recording", {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  const handlePause = () => {
    if (isDesktop) {
      void pauseRecording()
    } else {
      webRecorderRef.current?.pause()
      syncWebState('paused')
    }
  }

  const handleResume = () => {
    if (isDesktop) {
      void resumeRecording()
    } else {
      webRecorderRef.current?.resume()
      syncWebState('recording')
    }
  }

  const handleStop = async () => {
    setIsBusy(true)
    try {
      if (!isDesktop) {
        setIsOpen(false)
        await handleWebStop()
        return
      }
      await stopRecording()
      setIsOpen(false)
      toast.success('Recording stopped', {
        description: 'Uploading, then Benny writes the notes…',
      })
    } catch (error) {
      toast.error("Couldn't stop the recording", {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  // Discard the in-progress recording without uploading or generating notes
  // (the audio is thrown away) — like deleting an active timer.
  const handleDiscard = async () => {
    setIsBusy(true)
    try {
      if (!isDesktop) {
        const activeId = webRecordingIdRef.current
        webRecorderRef.current?.cancel()
        webRecorderRef.current = null
        if (activeId) clearStoredScratchpad(activeId)
        syncWebState('idle')
        webRecordingIdRef.current = null
        setIsOpen(false)
        toast.success('Recording discarded')
        return
      }
      // Flag the active recording so the auto-uploader skips it and deletes
      // the local file once the worker finishes finalizing.
      const activeId = useRecordingStore.getState().recordingId
      if (activeId) useRecordingStore.getState().markDiscarded(activeId)
      await stopRecording()
      setIsOpen(false)
      toast.success('Recording discarded')
    } catch (error) {
      toast.error("Couldn't discard the recording", {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  useRecordingControls({
    pause: handlePause,
    resume: handleResume,
    stop: handleStop,
    discard: handleDiscard,
  })

  // After all hooks (rules-of-hooks) — unsupported platform or flag off
  if (!isSupported || !checkFeatureFlag('MEETING_NOTES')) {
    return null
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            data-record-button
            className={cn(
              'transition-tonal relative h-8 gap-1.5 border px-3 text-sm duration-200 hover:scale-105 hover:shadow-md',
              useDarkText
                ? 'border-black/30 bg-black/5 text-black hover:bg-black/10'
                : 'border-white/30 bg-white/5 text-white hover:bg-white/10',
              isActive && 'text-red-500',
            )}
            title={
              isActive
                ? 'Recording meeting…'
                : hasNotesToReview
                  ? 'Meeting notes ready to review'
                  : 'Record meeting'
            }
          >
            {state === 'finalizing' ? (
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
            ) : isActive ? (
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  isRecording && 'animate-pulse bg-red-500',
                  isPaused && 'bg-amber-500',
                )}
              />
            ) : (
              <RecordIcon className="h-4 w-4" />
            )}
            {isActive ? (
              <span className="font-mono text-xs tabular-nums">
                <RecordingElapsed />
              </span>
            ) : (
              <span>Record</span>
            )}
            {isRecording && <RecordingVuBars source="combined" />}
            {hasNotesToReview && (
              <span
                data-notes-ready
                className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background"
              >
                <span className="sr-only">Meeting notes ready to review</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // Two tones: the canvas frames white cards.
          className="bg-canvas w-[min(22rem,calc(100vw-2rem))] overflow-hidden border-border p-1.5 shadow-[var(--shadow-floating)]"
          padding="none"
          align="end"
          onOpenAutoFocus={event => event.preventDefault()}
        >
          {/* PopoverContent wraps children in its own box, so the canvas
              gutters between cards live on this column. */}
          <div className="flex flex-col gap-1.5">
            {/* Status: what is happening and whether both sides are heard */}
            <div className="px-4 pb-1.5 pt-2.5">
              <div className="flex items-center gap-2">
                {isActive && (
                  <span
                    className={cn(
                      'h-2 w-2 shrink-0 rounded-full',
                      isRecording && 'animate-pulse bg-red-500',
                      isPaused && 'bg-amber-500',
                      state === 'finalizing' && 'bg-muted-foreground',
                    )}
                  />
                )}
                <p className="flex-1 text-sm font-semibold">
                  {!isActive
                    ? 'Record meeting'
                    : isPaused
                      ? 'Paused'
                      : state === 'finalizing'
                        ? 'Finishing up…'
                        : 'Recording'}
                </p>
                {isActive && (
                  <span className="font-mono text-sm tabular-nums text-muted-foreground">
                    <RecordingElapsed />
                  </span>
                )}
              </div>
              {isActive ? (
                <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    You
                    <RecordingVuBars source="mic" />
                  </span>
                  {/* "Others" is system audio: only meaningful on desktop with
                    system-audio capture (the web records the mic alone). */}
                  {!isDesktop ? null : systemAudioCaptured === false ? (
                    <span
                      className="text-amber-700 dark:text-amber-300"
                      title="System audio is off, so other participants aren’t being captured."
                    >
                      Mic only · others aren’t captured
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      Others
                      <RecordingVuBars source="system" />
                    </span>
                  )}
                </div>
              ) : (
                !isDesktop && (
                  <p className="mt-1 text-xs leading-snug text-muted-foreground">
                    The web records your mic only. Use the desktop app to
                    capture everyone.
                  </p>
                )
              )}
            </div>

            {/* Idle: did the last meeting's notes come through? */}
            {!isActive && (
              <LastRecordingRow
                recording={latest.recording}
                isReadyToReview={latest.isReadyToReview}
                onOpenNote={latest.openNote}
                onRetry={latest.retryRecording}
                onNavigate={() => setIsOpen(false)}
                onDismiss={latest.dismiss}
                className={LAST_RECORDING_CARD}
              />
            )}

            {/* Live captions: folded away until wanted, with a switch to start
              or stop them mid-meeting; the full wall lives in the panel */}
            {isActive &&
              (captionsActive !== null ||
                captions.length > 0 ||
                (isDesktop && captionModel?.supported)) && (
                <div className={MENU_CARD}>
                  <RecordingSectionToggle
                    label="Live captions"
                    summary={
                      captionsActive === 'starting'
                        ? 'Starting…'
                        : captionsActive === true
                          ? captions.length > 0
                            ? `${captions.length} line${captions.length === 1 ? '' : 's'}`
                            : 'Listening…'
                          : captionsActive === false
                            ? 'Couldn’t start'
                            : 'Off'
                    }
                    open={showCaptions}
                    onToggle={() => setShowCaptions(open => !open)}
                    actions={<LiveCaptionsSwitch />}
                  />
                  {showCaptions && !captionsActive && captions.length === 0 && (
                    <p className="px-4 pb-3 text-xs text-muted-foreground">
                      Turn on to see who says what, live. Captions run on this
                      device.
                    </p>
                  )}
                  {showCaptions && (captionsActive || captions.length > 0) && (
                    <div className="px-4 pb-3">
                      <p
                        className={cn(
                          'line-clamp-3 text-[13px] leading-snug',
                          captions.length === 0 &&
                            'italic text-muted-foreground',
                        )}
                      >
                        {captions.length > 0
                          ? captions[captions.length - 1].text
                          : captionsActive === 'starting'
                            ? 'Starting live captions…'
                            : 'Listening…'}
                      </p>
                      <div className="mt-2.5 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 flex-1 gap-1.5 text-xs"
                          title="See every caption in the side panel"
                          onClick={() => {
                            useRecordingStore.getState().setLivePanelOpen(true)
                            setIsOpen(false)
                          }}
                        >
                          <SidebarSimpleIcon className="h-3.5 w-3.5 -scale-x-100" />
                          Open in panel
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 flex-1 gap-1.5 text-xs"
                          disabled={
                            captions.length === 0 ||
                            catchUpCredits?.canUse === false
                          }
                          title={
                            catchUpCredits
                              ? `Summarize what you missed · ${catchUpCredits.requiredCredits} AI credit${catchUpCredits.requiredCredits === 1 ? '' : 's'}`
                              : 'Summarize what you missed'
                          }
                          onClick={() => void handleCatchUp()}
                        >
                          <SparkleIcon className="h-3.5 w-3.5" />
                          Catch me up
                        </Button>
                      </div>
                      {catchUpCredits?.canUse === false && (
                        <AICreditNotice
                          status={catchUpCredits}
                          compact
                          className="mt-2"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

            {/* Where the note lands and how it's written. Set up front before
              recording; folded to a one-line summary once it is running. */}
            <div className={MENU_CARD}>
              {isActive && (
                <RecordingSectionToggle
                  label="Note settings"
                  summary={linkedModule?.moduleName || 'My Notes'}
                  open={showNoteSettings}
                  onToggle={() => setShowNoteSettings(open => !open)}
                />
              )}
              {(!isActive || showNoteSettings) && (
                <div className={cn('space-y-3 px-4 pb-3', !isActive && 'pt-3')}>
                  <div className="space-y-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        Add note to
                      </span>
                      {!linkedModule && (
                        <span className="text-[11px] text-muted-foreground">
                          Otherwise saved to My Notes
                        </span>
                      )}
                    </div>
                    <LinkWorkSelector
                      value={linkValue}
                      onSelect={handleModuleSelect}
                      compact
                      closeOnSelect
                      portalToBody
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        className="text-xs font-medium text-muted-foreground"
                        title="Shapes how Benny writes the notes"
                      >
                        Summary style
                      </span>
                      {activeEventKey && (
                        <span className="text-[11px] text-muted-foreground">
                          Saved for this meeting series
                        </span>
                      )}
                    </div>
                    <SummaryTemplateSelect
                      value={summaryTemplate}
                      onChange={handleTemplateChange}
                      disabled={state === 'finalizing'}
                    />
                  </div>

                  {isDesktop && captionModel?.supported && !isActive && (
                    <label className="flex cursor-pointer items-center gap-2 pt-0.5">
                      <Checkbox
                        checked={
                          liveCaptionsEnabled && captionModel.state === 'ready'
                        }
                        disabled={captionDownloadPct !== null}
                        onCheckedChange={checked =>
                          void handleCaptionsToggle(checked === true)
                        }
                      />
                      <span className="text-sm">Live captions</span>
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        {captionDownloadPct !== null
                          ? `Downloading… ${captionDownloadPct}%`
                          : captionModel.state !== 'ready'
                            ? 'On-device · 142MB download'
                            : 'On-device · free'}
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>

            {/* Quick actions */}
            {isActive && (
              <div className="grid grid-cols-2 gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="hover:bg-surface h-8 justify-start gap-1.5 px-4 text-xs"
                  title="Your notes lead the summary and are kept verbatim as a page"
                  onClick={() => {
                    useRecordingStore.getState().setLivePanelOpen(true)
                    setIsOpen(false)
                  }}
                >
                  <PencilSimpleLineIcon className="h-3.5 w-3.5" />
                  {scratchpad.trim() ? 'Continue notes' : 'Take notes'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="hover:bg-surface h-8 justify-start gap-1.5 px-4 text-xs"
                  title="Copy a message letting everyone know they're being recorded"
                  onClick={() => void copyRecordingNotice()}
                >
                  <ChatCircleTextIcon className="h-3.5 w-3.5" />
                  Copy notice
                </Button>
              </div>
            )}

            {/* Transport */}
            <div>
              {isActive ? (
                <div className="flex items-center gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={isBusy || state === 'finalizing'}
                    onClick={() => void handleDiscard()}
                    aria-label="Discard recording"
                    title="Discard recording (audio is deleted, no notes generated)"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                  {isPaused ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-surface flex-1 gap-1.5"
                      disabled={isBusy}
                      onClick={handleResume}
                    >
                      <PlayIcon className="h-3.5 w-3.5" />
                      Resume
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-surface flex-1 gap-1.5"
                      disabled={isBusy || state === 'finalizing'}
                      onClick={handlePause}
                    >
                      <PauseIcon className="h-3.5 w-3.5" />
                      Pause
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    className="flex-1 gap-1.5"
                    disabled={isBusy || state === 'finalizing'}
                    onClick={() => void handleStop()}
                  >
                    <StopIcon className="h-3.5 w-3.5" weight="fill" />
                    Stop
                  </Button>
                </div>
              ) : (
                <Button
                  className="w-full gap-2"
                  disabled={isBusy}
                  onClick={() => void handleStart()}
                >
                  {isBusy ? (
                    <CircleNotchIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <RecordIcon className="h-4 w-4" weight="fill" />
                  )}
                  Start recording
                </Button>
              )}
            </div>

            <button
              type="button"
              className="rounded-control hover:bg-surface flex w-full items-center justify-between px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => {
                setIsOpen(false)
                if (orgSlug) navigate(`/${orgSlug}/notes/recordings`)
              }}
            >
              <span className="flex items-center gap-1.5">
                <ListBulletsIcon className="h-3.5 w-3.5" />
                View all recordings
              </span>
              <CaretRightIcon className="h-3 w-3" />
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {isDesktop && (
        <RecordingPermissionsDialog
          open={showPermissionsDialog}
          onOpenChange={setShowPermissionsDialog}
          onGranted={() => {
            setShowPermissionsDialog(false)
            void handleStart()
          }}
        />
      )}

      <DialogPattern
        open={catchUpOpen}
        onOpenChange={setCatchUpOpen}
        title="Catch me up"
        description="What you missed in this meeting so far."
        footer={null}
      >
        {isCatchingUp ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
            Summarizing the conversation…
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">
            {catchUpSummary}
          </p>
        )}
      </DialogPattern>
    </>
  )
}
